/**
 * Nespresso TES Sync Hook
 *
 * Syncs Nespresso gamification state with TES holder data.
 * - On login: Loads lifetimePods and achievedTiers from TES
 * - On state change: Syncs updated state to TES
 * - On milestone: Emits milestone_achieved event
 *
 * Note: User-specific features require a user ID (e.g., from session or email).
 * Without a user ID, sync operations will be skipped gracefully.
 */

import { useCallback, useRef } from 'react';
import { getHolder, updateHolder, createHolder, addThingStatus, type HolderInput } from '../lib/tes-client';

// Gamification data stored in TES holder.data
export interface NespressoGamificationData {
  nespresso_lifetime_pods?: number;
  nespresso_achieved_tiers?: string[];
  nespresso_last_sync?: number;
}

export interface GamificationState {
  lifetimePods: number;
  achievedTiers: string[];
}

interface UseNespressoTESSyncOptions {
  /** Whether to enable TES sync (default: true) */
  enabled?: boolean;
  /** User ID for holder operations (optional) */
  userId?: string;
}

export function useNespressoTESSync(options: UseNespressoTESSyncOptions = {}) {
  const syncInProgressRef = useRef(false);
  const lastSyncedStateRef = useRef<string>('');

  // Always enabled by default (auth handled server-side)
  const isEnabled = options.enabled ?? true;
  const userId = options.userId;

  /**
   * Load gamification state from TES
   * Returns the stored state or null if not found/error
   */
  const loadFromTES = useCallback(async (): Promise<GamificationState | null> => {
    if (!isEnabled) {
      console.log('[TES Sync] Sync disabled - skipping load');
      return null;
    }

    if (!userId) {
      console.log('[TES Sync] No user ID - skipping load');
      return null;
    }

    try {
      console.log('[TES Sync] Loading gamification state for user:', userId);
      const holder = await getHolder(userId);

      if (holder?.data) {
        const data = holder.data as NespressoGamificationData;
        const state: GamificationState = {
          lifetimePods: data.nespresso_lifetime_pods ?? 0,
          achievedTiers: data.nespresso_achieved_tiers ?? ['first-sip'],
        };
        console.log('[TES Sync] Loaded state:', state);
        return state;
      }

      console.log('[TES Sync] No existing holder data found');
      return null;
    } catch (error) {
      console.error('[TES Sync] Error loading state:', error);
      return null;
    }
  }, [isEnabled, userId]);

  /**
   * Save gamification state to TES
   */
  const saveToTES = useCallback(async (state: GamificationState): Promise<boolean> => {
    if (!isEnabled) {
      console.log('[TES Sync] Sync disabled - skipping save');
      return true;
    }

    // Debounce: skip if same state was just synced
    const stateKey = JSON.stringify(state);
    if (stateKey === lastSyncedStateRef.current) {
      console.log('[TES Sync] State unchanged - skipping save');
      return true;
    }

    // Prevent concurrent syncs
    if (syncInProgressRef.current) {
      console.log('[TES Sync] Sync already in progress - skipping');
      return true;
    }

    if (!userId) {
      console.log('[TES Sync] No user ID - skipping save');
      return false;
    }

    syncInProgressRef.current = true;

    try {
      console.log('[TES Sync] Saving gamification state for user:', userId);

      const holderData: NespressoGamificationData = {
        nespresso_lifetime_pods: state.lifetimePods,
        nespresso_achieved_tiers: state.achievedTiers,
        nespresso_last_sync: Date.now(),
      };

      const input: HolderInput = {
        type: 'customer',
        data: holderData as Record<string, unknown>,
      };

      // Try to update existing holder first
      const result = await updateHolder(userId, input);

      if (result.success) {
        console.log('[TES Sync] State saved successfully');
        lastSyncedStateRef.current = stateKey;
        return true;
      }

      // If holder doesn't exist, create it
      console.log('[TES Sync] Holder not found, creating new one');
      const createResult = await createHolder({
        id: userId,
        type: 'customer',
        data: holderData as Record<string, unknown>,
      });

      if (createResult.success) {
        console.log('[TES Sync] Holder created successfully');
        lastSyncedStateRef.current = stateKey;
        return true;
      }

      console.warn('[TES Sync] Failed to save state:', createResult.message);
      return false;
    } catch (error) {
      console.error('[TES Sync] Error saving state:', error);
      return false;
    } finally {
      syncInProgressRef.current = false;
    }
  }, [isEnabled, userId]);

  /**
   * Emit milestone achieved event
   */
  const emitMilestoneAchieved = useCallback(async (
    tierId: string,
    tierName: string,
    lifetimePods: number
  ): Promise<boolean> => {
    if (!isEnabled) {
      console.log('[TES Sync] Sync disabled - skipping milestone event');
      return true;
    }

    if (!userId) {
      console.log('[TES Sync] No user ID - skipping milestone event');
      return false;
    }

    try {
      console.log('[TES Sync] Emitting milestone achieved:', tierId);

      // Create a "milestone" thing representing the achievement
      const milestoneId = `nespresso_milestone_${userId}_${tierId}_${Date.now()}`;

      const result = await addThingStatus(milestoneId, {
        parent_status: 'processed',
        status: 'milestone_achieved',
        holder_id: userId,
        data: {
          program: 'nespresso',
          tier_id: tierId,
          tier_name: tierName,
          lifetime_pods: lifetimePods,
          achieved_at: Date.now(),
        },
      });

      if (result.success) {
        console.log('[TES Sync] Milestone event emitted:', result.eventId);
        return true;
      }

      console.warn('[TES Sync] Failed to emit milestone:', result.message);
      return false;
    } catch (error) {
      console.error('[TES Sync] Error emitting milestone:', error);
      return false;
    }
  }, [isEnabled, userId]);

  return {
    isEnabled,
    loadFromTES,
    saveToTES,
    emitMilestoneAchieved,
  };
}

export default useNespressoTESSync;
